import React, {useState} from "react";
import PropTypes from "prop-types";
import {Box, FormControlLabel, Switch, Typography} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useMutation} from "@apollo/client";
import {getUserMemoryEntries, updateUserMemoryEntry, deleteUserMemoryEntry} from "../../../api";
import MemoryDialog from "../../../components/dialogs/MemoryDialog";
import StyledTooltip from "../../../components/tooltips/StyledTooltip";
import {ALERT} from "../../../resources/environment";
import {useAIServices} from "../../../context/AIServicesHooks";
import {useUser} from "../../../components/user/Context/UserProvider";
import {SET_USER_SETTING} from "../../../graphql/userQueries";
import {getUserSettingBool} from "../../../resources/users";
import {reject} from "underscore";

const UserMemoryDialog = ({isOpen, onClose}) => {
  const {apiClient} = useAIServices();
  const {user, setUser} = useUser();
  const [memoryEnabled, setMemoryEnabled] = useState(
    !getUserSettingBool(user, "wantsUserMemoryDisabled", false),
  );

  const [updateUserSetting] = useMutation(SET_USER_SETTING, {
    onCompleted: (data) => {
      const newSetting = data?.setUserSetting;
      if (!newSetting || newSetting.key !== "wantsUserMemoryDisabled") {
        return;
      }
      setUser({
        ...user,
        settings: [
          ...reject(user.settings, (setting) => setting.key === "wantsUserMemoryDisabled"),
          newSetting,
        ],
      });
    },
    onError: () => {
      setMemoryEnabled(!getUserSettingBool(user, "wantsUserMemoryDisabled", false));
      ALERT.error("Could not update memory preference.");
    },
  });

  const handleMemoryToggle = (enabled) => {
    setMemoryEnabled(enabled);
    updateUserSetting({
      variables: {userId: user.id, key: "wantsUserMemoryDisabled", value: `${!enabled}`},
    });
  };

  const fetchEntries = () => getUserMemoryEntries(apiClient, {email: user?.email});

  const updateEntry = (memoryId, content) =>
    updateUserMemoryEntry(apiClient, {
      email: user?.email,
      memoryId,
      content,
      userName: user?.nickname || user?.fullName || user?.email,
    });

  const deleteEntry = (memoryId) =>
    deleteUserMemoryEntry(apiClient, {memoryId, email: user?.email});

  const headerContent = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        Rex AI captures personal context from your Maestro conversations.
      </Typography>
      <Box display="flex" alignItems="center">
        <StyledTooltip
          title={
            "Your memory entries are private to you and " +
            "cannot be viewed by your organization's admins."
          }
          placement="left"
        >
          <InfoOutlinedIcon color="primary" fontSize="small" />
        </StyledTooltip>
        <FormControlLabel
          label={memoryEnabled ? "My Memory Enabled" : "My Memory Disabled"}
          labelPlacement="start"
          control={(
            <Switch
              size="small"
              checked={memoryEnabled}
              onChange={(e) => handleMemoryToggle(e.target.checked)}
              sx={{ml: 1}}
            />
          )}
        />
      </Box>
    </Box>
  );

  return (
    <MemoryDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Manage My Memory"
      emptyText={
        "No personal memory entries yet. " +
        "They'll be captured automatically during Maestro conversations."
      }
      fetchEntries={fetchEntries}
      updateEntry={updateEntry}
      deleteEntry={deleteEntry}
      headerContent={headerContent}
    />
  );
};

UserMemoryDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UserMemoryDialog;
